import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { withTrackedReducer } from '@angular-architects/ngrx-toolkit';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import {
  Events,
  mapToScope,
  on,
  withEventHandlers,
} from '@ngrx/signals/events';
import { peppolStatusEvents } from '../../../store/peppol.events';
import { mapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver-es';
import { base64ToBlob, NotificationService } from 'wflow-cdk';
import { EMPTY } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  ignoreElements,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  PeppolAttachment,
  PeppolDetail,
} from '../../../interfaces/peppol-detail.interface';
import { PeppolItem } from '../../../interfaces/peppol-item.interface';
import { PeppolUblPreview } from '../../../interfaces/peppol-ubl-preview.interface';
import { PeppolAction } from '../../../interfaces/peppol-action.interface';
import { PEPPOL_ACTION_API_PATHS } from '../../../const/peppol-message-actions.const';
import { PEPPOL_MESSAGES_API } from '../../../tokens/peppol-messages-api.token';
import {
  peppolDetailApiEvents,
  peppolDetailPageEvents,
} from './peppol-detail.events';
import { PEPPOL_XML_CONTENT_TYPE } from '../peppol-detail-dialog.const';
import { withStoreDevtools } from 'wflow-main-app/src/environments';

interface PeppolDetailState {
  messageId: string | null;
  detail: PeppolDetail | null;
  message: PeppolItem | null;
  attachments: PeppolAttachment[];
  ublPreview: PeppolUblPreview | null;
  activeFileName: string | null;
  loading: boolean;
  sendingStatus: boolean;
  loadError: HttpErrorResponse | Error | null;
}

const initialState: PeppolDetailState = {
  messageId: null,
  detail: null,
  message: null,
  attachments: [],
  ublPreview: null,
  activeFileName: null,
  loading: false,
  sendingStatus: false,
  loadError: null,
};

export const PeppolDetailStore = signalStore(
  withStoreDevtools('peppol-detail'),
  withState<PeppolDetailState>(initialState),
  withComputed((store) => ({
    activeAttachment: computed<PeppolAttachment | null>(
      () =>
        store
          .attachments()
          .find((attachment) => attachment.name === store.activeFileName()) ??
        null,
    ),
  })),
  withTrackedReducer(
    on(peppolDetailPageEvents.initialized, ({ payload }) => ({
      messageId: payload.messageId,
      loading: true,
      loadError: null,
    })),
    on(peppolDetailApiEvents.detailLoaded, ({ payload }) => ({
      detail: payload.detail,
      message: payload.message,
      attachments: payload.attachments,
      ublPreview: payload.ublPreview,
      loading: false,
      activeFileName: pickActiveFileName(payload.attachments, null),
    })),
    on(peppolDetailApiEvents.detailReloaded, ({ payload }, state) => ({
      detail: payload.detail,
      message: payload.message,
      attachments: withLoadedContent(state.attachments, payload.attachments),
      ublPreview: payload.ublPreview,
      activeFileName: pickActiveFileName(
        payload.attachments,
        state.activeFileName,
      ),
      sendingStatus: false,
    })),
    on(
      peppolDetailPageEvents.statusActionConfirmed,
      peppolDetailPageEvents.rejectConfirmed,
      () => ({ sendingStatus: true }),
    ),
    on(
      peppolDetailApiEvents.actionFailed,
      peppolDetailApiEvents.detailReloadFailed,
      () => ({ sendingStatus: false }),
    ),
    on(peppolDetailApiEvents.detailLoadFailed, ({ payload }) => ({
      loading: false,
      loadError: payload.error,
    })),
    on(peppolDetailPageEvents.fileViewed, ({ payload }) => ({
      activeFileName: payload.name,
    })),
    on(peppolDetailApiEvents.attachmentContentLoaded, ({ payload }, state) => ({
      attachments: state.attachments.map((attachment) =>
        attachment.name === payload.name
          ? { ...attachment, content: payload.content }
          : attachment,
      ),
    })),
  ),
  withEventHandlers((store) => {
    const events = inject(Events);
    const peppolMessagesApi = inject(PEPPOL_MESSAGES_API);
    const notification = inject(NotificationService);
    const translate = inject(TranslateService);

    const toErrorText = (error?: HttpErrorResponse | Error): string => {
      const body =
        error instanceof HttpErrorResponse ? error.error?.message : null;

      return (
        body ??
        error?.message ??
        translate.instant('BasicErrorHandlerService_DefaultErrorTitle')
      );
    };

    return {
      fetchDetail$: events.on(peppolDetailPageEvents.initialized).pipe(
        switchMap(({ payload }) =>
          peppolMessagesApi.getDetail(payload.messageId).pipe(
            mapResponse({
              next: (detail) => peppolDetailApiEvents.detailLoaded(detail),
              error: (error: HttpErrorResponse | Error) =>
                peppolDetailApiEvents.detailLoadFailed({ error }),
            }),
          ),
        ),
      ),

      fetchAttachmentContent$: events
        .on(peppolDetailPageEvents.attachmentRequested)
        .pipe(
          filter(
            ({ payload }) =>
              !findLoadedAttachment(store.attachments(), payload.name),
          ),
          switchMap(({ payload }) => {
            const messageId = store.messageId();
            return peppolMessagesApi
              .getAttachmentContent(messageId ?? '', payload.name)
              .pipe(
                mapResponse({
                  next: (attachment) =>
                    peppolDetailApiEvents.attachmentContentLoaded({
                      name: payload.name,
                      content: attachment.content ?? '',
                    }),
                  error: (error: HttpErrorResponse) =>
                    peppolDetailApiEvents.attachmentContentFailed({
                      name: payload.name,
                      error,
                    }),
                }),
              );
          }),
        ),

      downloadAttachment$: events
        .on(peppolDetailPageEvents.downloadRequested)
        .pipe(
          switchMap(({ payload }) => {
            const cached = findLoadedAttachment(
              store.attachments(),
              payload.name,
            );
            if (cached) {
              downloadAttachment(cached);
              return EMPTY;
            }
            return peppolMessagesApi
              .getAttachmentContent(store.messageId() ?? '', payload.name)
              .pipe(
                tap((attachment: PeppolAttachment) =>
                  downloadAttachment(attachment),
                ),
                ignoreElements(),
                catchError((error: HttpErrorResponse) => {
                  notification.error({ text: toErrorText(error) });
                  return EMPTY;
                }),
              );
          }),
        ),

      notifyAttachmentFailure$: events
        .on(peppolDetailApiEvents.attachmentContentFailed)
        .pipe(
          tap(({ payload }) =>
            notification.error({ text: toErrorText(payload.error) }),
          ),
        ),

      runStatusAction$: events
        .on(peppolDetailPageEvents.statusActionConfirmed)
        .pipe(
          exhaustMap(({ payload: { action } }) => {
            const message = store.message();
            const apiPath = PEPPOL_ACTION_API_PATHS[action];
            if (!message || !apiPath) {
              return EMPTY;
            }
            return peppolMessagesApi.sendItemAction(message, apiPath).pipe(
              mapResponse({
                next: () => peppolDetailApiEvents.actionSucceeded({ action }),
                error: (error: HttpErrorResponse) =>
                  peppolDetailApiEvents.actionFailed({ action, error }),
              }),
            );
          }),
        ),

      runReject$: events.on(peppolDetailPageEvents.rejectConfirmed).pipe(
        exhaustMap(({ payload: { request } }) =>
          peppolMessagesApi.rejectItem(store.messageId() ?? '', request).pipe(
            mapResponse({
              next: () =>
                peppolDetailApiEvents.actionSucceeded({
                  action: PeppolAction.Reject,
                }),
              error: (error: HttpErrorResponse) =>
                peppolDetailApiEvents.actionFailed({
                  action: PeppolAction.Reject,
                  error,
                }),
            }),
          ),
        ),
      ),

      reloadDetail$: events.on(peppolDetailApiEvents.actionSucceeded).pipe(
        switchMap(() =>
          peppolMessagesApi.getDetail(store.messageId() ?? '').pipe(
            mapResponse({
              next: (detail) => peppolDetailApiEvents.detailReloaded(detail),
              error: (error: HttpErrorResponse | Error) =>
                peppolDetailApiEvents.detailReloadFailed({ error }),
            }),
          ),
        ),
      ),

      notifyReloadFailure$: events
        .on(peppolDetailApiEvents.detailReloadFailed)
        .pipe(
          tap(({ payload }) =>
            notification.error({ text: toErrorText(payload.error) }),
          ),
        ),

      notifyActionSuccess$: events
        .on(peppolDetailApiEvents.actionSucceeded)
        .pipe(
          tap(({ payload }) =>
            notification.success({
              title: translate.instant(`PEPPOL.ACTIONS.${payload.action}`),
              text: translate.instant(
                `PEPPOL.ACTION_RESULTS.${payload.action}`,
              ),
            }),
          ),
        ),

      notifyOpener$: events.on(peppolDetailApiEvents.actionSucceeded).pipe(
        map(() => peppolStatusEvents.changed()),
        mapToScope('parent'),
      ),

      notifyActionFailure$: events.on(peppolDetailApiEvents.actionFailed).pipe(
        tap(({ payload }) =>
          notification.error({
            text:
              payload.error?.error?.description ??
              translate.instant('PEPPOL.ACTION_FAILED'),
            title: translate.instant(`PEPPOL.ACTIONS.${payload.action}`),
          }),
        ),
      ),
    };
  }),
);

function downloadAttachment(attachment: PeppolAttachment): void {
  if (!attachment.content) {
    return;
  }
  saveAs(
    base64ToBlob(attachment.content, attachment.contentType),
    attachment.name,
  );
}

function withLoadedContent(
  previous: PeppolAttachment[],
  next: PeppolAttachment[],
): PeppolAttachment[] {
  return next.map((attachment) => {
    const cached = findLoadedAttachment(previous, attachment.name);
    return cached ? { ...attachment, content: cached.content } : attachment;
  });
}

function pickActiveFileName(
  attachments: PeppolAttachment[],
  current: string | null,
): string | null {
  if (current && attachments.some(({ name }) => name === current)) {
    return current;
  }

  return (
    attachments.find(
      ({ contentType }) => contentType === PEPPOL_XML_CONTENT_TYPE,
    )?.name ??
    attachments[0]?.name ??
    null
  );
}

function findLoadedAttachment(
  attachments: PeppolAttachment[],
  name: string,
): PeppolAttachment | undefined {
  return attachments.find(
    (attachment) => attachment.name === name && !!attachment.content,
  );
}
